<template>
  <div :class="classResolver(classes)" class="dashboard-grid" :style="gridStyle">
    <div
      v-for="cell in cells"
      :key="cell.component.id"
      class="dashboard-grid__cell"
      :style="cellStyle(cell.layout)"
    >
      <component
        :is="cell.component.name"
        :classes="cell.component.classes"
        :componentData="cell.component"
        :componentId="cell.component.id"
        :components="cell.component.components"
        v-bind="cell.component.props"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';
import classResolver from '@apexl/icarus-core/src/services/classResolver';

// Icarus counterpart of `apexl\Io\modules\dynamicComponents\Component\DashboardGrid`
// (registry NAME `DashboardGrid`) — see that class's docblock for the
// `layout`/`components` contract (one `layout` entry per child, matched by
// stringified array index `i`).
//
// Renders via plain CSS grid rather than `vue-grid-layout-v3` (already a
// project dependency, used by the legacy `DashboardView.vue` panel system)
// deliberately: `TileGridRenderer::build()` (api/module/shell/Service/
// TileGridRenderer.php) never sets a tile height — `h` is always `null` on
// the wire — because content-driven tile heights aren't known server-side.
// A pixel-row grid library needs a numeric height per item; CSS grid's
// `grid-auto-rows` doesn't, so this stays correct for arbitrary tile
// content without inventing fake heights. `rowHeight` is honoured as a
// minimum row height so short tiles still get reasonable breathing room.
// If/when the layout editor (drag/resize) lands, that's a distinct
// interactive mode and a good place to reach for vue-grid-layout-v3 — this
// component is the read-only live-dashboard renderer only.
const props = defineProps({
  ...commonProps,
  layout: {
    type: Array,
    default: () => ([]),
  },
  rowHeight: {
    type: Number,
    default: 40,
  },
  components: {
    type: Array,
    default: () => ([]),
  },
});

const cells = computed(() => props.layout
  .map(layoutEntry => {
    const component = props.components[parseInt(layoutEntry.i, 10)];

    return component ? { layout: layoutEntry, component } : null;
  })
  .filter(cell => cell !== null));

const gridStyle = computed(() => ({
  '--dg-row-height': `${props.rowHeight}px`,
}));

function cellStyle(layoutEntry) {
  const x = Number(layoutEntry.x ?? 0);
  const y = Number(layoutEntry.y ?? 0);
  const w = Math.max(1, Number(layoutEntry.w ?? 1));

  return {
    gridColumn: `${x + 1} / span ${w}`,
    gridRow: `${y + 1}`,
  };
}
</script>

<style lang="scss">
.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-auto-rows: minmax(var(--dg-row-height, 40px), auto);
  gap: 16px;

  &__cell {
    min-width: 0;
  }
}
</style>
